import { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { toast } from "react-toastify";
import { LOCALES, t } from "../../i18n/messages";
import { useAppState } from "../../state/AppStateProvider";
import { buildPath, stripLangFromPath } from "../../utils/routing";
import { useWebsocketMessages } from "../../hooks/useWsConnection";

const NAV_ITEMS = [
  { key: "navHome", slug: "" },
  { key: "navBlog", slug: "blog" },
  { key: "navAuction", slug: "auction" },
  { key: "navMarket", slug: "market" },
  // { key: "navDetail", slug: "detail" },
  { key: "navRwa", slug: "rwa" },
  { key: "navSettlement", slug: "settlement" },
  { key: "navProfile", slug: "profile" },
  // { key: "navStudio", slug: "studio" },
];

export default function AppShell({ locale, pageKey, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { walletConnected, walletLinked, authStatus, authenticateWallet } =
    useAppState();
  const [menuOpen, setMenuOpen] = useState(false);

  const { localPath } = stripLangFromPath(location.pathname);
  const localSlug = localPath === "/" ? "" : localPath.replace(/^\//, "");

  const formatToastText = useCallback(
    (key, fallback, params = {}) => {
      let text = t(locale, key, fallback);
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        text = text.replaceAll(`{${paramKey}}`, String(paramValue ?? ""));
      });
      return text;
    },
    [locale],
  );

  const handleWsMessage = useCallback(
    (msg) => {
      if (msg?.type === "auction:event") {
        const eventType = String(msg?.event || "")
          .trim()
          .toLowerCase();
        if (
          eventType === "bid" ||
          eventType === "new" ||
          eventType === "cancel"
        ) {
          queryClient.invalidateQueries({ queryKey: ["auction", "list"] });
          queryClient.invalidateQueries({ queryKey: ["auction", "detail"] });
          queryClient.invalidateQueries({ queryKey: ["auction", "bids"] });
          queryClient.invalidateQueries({ queryKey: ["auction", "events"] });
          queryClient.invalidateQueries({ queryKey: ["profile", "auctions"] });
        }
        return;
      }

      if (msg?.type === "auction:listing") {
        const id = msg?.auctionId ? `#${msg.auctionId}` : "?";
        if (msg?.action === "new") {
          toast.info(
            formatToastText("appToastAuctionListed", "Auction {id} listed", {
              id,
            }),
          );
        } else if (msg?.action === "cancel") {
          toast.warn(
            formatToastText(
              "appToastAuctionCancelled",
              "Auction {id} cancelled",
              { id },
            ),
          );
        } else if (msg?.action === "settled") {
          toast.success(
            formatToastText("appToastAuctionSettled", "Auction {id} settled", {
              id,
            }),
          );
        }

        queryClient.invalidateQueries({ queryKey: ["auction", "list"] });
        queryClient.invalidateQueries({ queryKey: ["auction", "detail"] });
        queryClient.invalidateQueries({ queryKey: ["auction", "bids"] });
        queryClient.invalidateQueries({ queryKey: ["auction", "rebates"] });
        queryClient.invalidateQueries({ queryKey: ["auction", "events"] });
        return;
      }

      if (msg?.type === "nft:listing") {
        const nftAddress = String(msg?.nftAddress || "").trim();
        const tokenId = String(msg?.tokenId ?? "").trim();
        const shortNft = nftAddress
          ? `${nftAddress.slice(0, 6)}...${nftAddress.slice(-4)}`
          : "";
        const nftLabel = tokenId
          ? `#${tokenId}${shortNft ? ` (${shortNft})` : ""}`
          : shortNft || "NFT";

        if (msg?.action === "listed") {
          toast.info(
            formatToastText("appToastNftListed", "{nft} listed for sale", {
              nft: nftLabel,
            }),
          );
        } else if (msg?.action === "offloaded") {
          toast.warn(
            formatToastText("appToastNftDelisted", "{nft} delisted", {
              nft: nftLabel,
            }),
          );
        } else if (msg?.action === "sold") {
          toast.success(
            formatToastText("appToastNftSold", "{nft} sold", {
              nft: nftLabel,
            }),
          );
        }

        queryClient.invalidateQueries({ queryKey: ["market", "listed-nfts"] });
        queryClient.invalidateQueries({
          queryKey: ["market", "purchase-history"],
        });
        queryClient.invalidateQueries({
          queryKey: ["profile", "nft-listings"],
        });
      }
    },
    [formatToastText, queryClient],
  );

  useWebsocketMessages(handleWsMessage);

  const onLanguageChange = (nextLocale) => {
    const next = `${buildPath(nextLocale, localSlug)}${location.search}`;
    navigate(next);
  };

  return (
    <div className="app-root">
      <div className="bg-layer" />
      <header className="site-header">
        <Link className="brand" to={buildPath(locale, "")}>
          <img
            className="brand-logo"
            src="/assets/brand-logo.png"
            alt="Chronos Arte Node logo"
          />
        </Link>
        <nav
          className={`main-nav${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen(false)}
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.key}
              to={buildPath(locale, item.slug)}
              className={
                pageKey === item.slug ||
                (item.slug === "" && pageKey === "home")
                  ? "active"
                  : ""
              }
            >
              {t(locale, item.key)}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <select
            value={locale}
            onChange={(e) => onLanguageChange(e.target.value)}
            aria-label={t(locale, "appLanguageSelector")}
          >
            {Object.keys(LOCALES).map((lang) => (
              <option key={lang} value={lang}>
                {LOCALES[lang].label || lang}
              </option>
            ))}
          </select>

          <div className="wallet-action-group">
            <ConnectButton showBalance={false} accountStatus="address" />
            {walletLinked &&
            !walletConnected &&
            authStatus.state === "error" ? (
              <button className="btn btn-ghost" onClick={authenticateWallet}>
                {t(locale, "appRetrySignIn")}
              </button>
            ) : null}
          </div>
          <button
            className={`nav-toggle${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <main>{children}</main>

      <footer className="site-footer">
        <p>{t(locale, "appFooterText")}</p>
      </footer>
    </div>
  );
}
